<template>
  <div class="checkout-step-two">
    <div class="checkout-step__header">
      <div class="row align-items-end">
        <div class="col-12 col-md-9">
          <h3 class="checkout-step__headline">
            {{ $t('pages.checkoutStepTwo.title') }}
          </h3>
          <p class="checkout-step__copy">
            {{ $t('pages.checkoutStepTwo.copy') }}
          </p>
          <p class="checkout-step__copy-additional">
            {{ $t('pages.checkoutStepOne.copyAdditional') }}
          </p>
        </div>
        <div class="col-12 col-md-3">
          <small class="checkout-step__copy checkout-step__copy--small">
            {{ $t('generic.priceInfo') }}
          </small>
        </div>
      </div>
    </div>

    <div class="campsite-type-selector">
      <div class="row">
        <div
          class="col-sm-6"
          v-for="(campsiteType, index) in campsiteTypes"
          :key="index"
        >
          <div
            class="campsite-type white-box"
            :class="{
              active: steps[2].selectedCampsiteType === campsiteType[0],
            }"
            @click="setCampsiteType(campsiteType)"
          >
            {{ campsiteType[1] }}
          </div>
        </div>
      </div>
    </div>

    <div class="campsite-selector" ref="campsiteSelector">
      <CiAccommodation
        v-for="campsite in getAvailableCampsites"
        v-if="campsite.campsite_type === steps[2].selectedCampsiteType"
        :campsite="campsite"
        :campground="campground"
        :steps="steps"
        :status="status"
        :language="language"
        :active-step="activeStep"
        :currency="currency"
        :is-active="campsite.id === selectedCampingId"
        :key="campsite.id"
        :nights="numNights"
        :selectedSeasons="selectedSeasons"
        @to-step="goToStep"
        @select-campsite="isSelectedCampsite"
      ></CiAccommodation>
    </div>

    <CiProgressNavigation
      class="campground__navigation--inline"
      :active-step="activeStep"
      :status="status"
      :steps="steps"
    >
    </CiProgressNavigation>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { CiAccommodation, CiProgressNavigation } from '@/components'
import { isElementInViewport } from '@/utils/general'
import { getJsDate } from '@/utils/date'
import { mapGetters, mapActions } from 'vuex'

// import iconArrowBlack from '@/assets/icons/arrow-black.svg'
// import iconArrowWhite from '@/assets/icons/arrow-white.svg'

export default {
  name: 'CheckoutStepTwo',

  components: {
    CiAccommodation,
    CiProgressNavigation,
    // iconArrowBlack,
    // iconArrowWhite,
  },

  created() {
    this.trackEcommerceEvents(2)
  },

  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      currency: 'currency',
      language: 'language',
      steps: 'steps',
    }),

    ...mapGetters('booking', {
      dates: 'dates',
      selectedCampsite: 'selectedCampsite',
      selectedPersons: 'selectedPersons',
      selectedSeasons: 'selectedSeasons',
      selectedCampingId: 'selectedCampingId'
    }),

    ...mapGetters('campground', {
      availableCampsites: 'availableCampsites',
      campground: 'campground',
      campsiteTypes: 'campsiteTypes',
      status: 'status',
    }),

    getAvailableCampsites() {
      // const filtered = this.availableCampsites.filter(campsite => campsite.amount > 0)
      return this.availableCampsites.map(campsite => {
        const basicCampsite = this.campground.campsites.find(
          cp => cp.id === campsite.id
        )
        return { ...basicCampsite, ...campsite }
      })
    },

    numNights() {
      const d = this.dates
      if (!d || !d[1] || d[0] == null || d[1] == null) {
        return 0
      }

      const d1 = DateTime.fromJSDate(getJsDate(d[0]))
      const d2 = DateTime.fromJSDate(getJsDate(d[1]))

      return Math.round(Math.abs(d2.diff(d1, 'days').as('days')))
    },
  },

  methods: {
    ...mapActions('application', {
      setStepStatus: 'setStepStatus',
      trackEcommerceEvents: 'trackEcommerceEvents',
    }),

    ...mapActions('booking', {
      setSelectedCampsite: 'setSelectedCampsite',
      setSelectedCampingId: 'setSelectedCampingId',
    }),

    setCampsiteType(campsiteType) {
      // show warning modal if campsite is selected and users switches campsite type
      if (this.selectedCampsite) {
        this.$modal.show('dialog', {
          title: this.$t('modals.CiModalCampsiteTypeWarning.title'),
          content: this.$t('modals.CiModalCampsiteTypeWarning.content'),
          proceedEvent: /* istanbul ignore next */ () =>
            this.onChangeCampsiteType(campsiteType),
          selectedCampsite: this.selectedCampsite,
        })
      } else {
        // set selected campsite type into step status
        this.setStepStatus({
          number: 2,
          key: 'selectedCampsiteType',
          value: campsiteType[0],
        })

        // scroll campsites into viewport if they aren't
        this.$nextTick(() => {
          /* istanbul ignore else */
          if (!isElementInViewport(this.$refs.campsiteSelector)) {
            this.$refs.campsiteSelector.scrollIntoView({
              block: 'start',
              behavior: 'smooth',
            })
          }
        })
      }
    },

    // if user agrees to change campsite type inside the warning modal
    onChangeCampsiteType(campsiteType) {
      this.setSelectedCampsite(null)
      this.setStepStatus({
        number: 2,
        key: 'selectedCampsiteType',
        value: campsiteType[0],
      })
    },

    isSelectedCampsite(campsite) {
      this.setSelectedCampsite(campsite)
      this.setSelectedCampingId(campsite.id)
    },

    getWeekdaysString(days) {
      let names = []
      let idx = 0
      let hasSunday = false
      for (let day = 4; day <= 10; day++) {
        if (days.indexOf(idx) !== -1) {
          if (idx === 0) {
            hasSunday = true
          } else {
            names.push(
              new Date(1970, 0, day).toLocaleString(this.language.code, {
                weekday: 'long',
              })
            )
          }
        }
        idx++
      }
      if (hasSunday) {
        names.push(
          new Date(1970, 0, 4).toLocaleString(this.language.code, {
            weekday: 'long',
          })
        )
      }
      return names.join(', ')
    },

    goToStep(stepData) {
      this.setStepStatus(stepData)
      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: stepData.number },
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.checkout-step-two {
  padding: 0;

  @include media-breakpoint-up(md) {
    padding: 0 0 60px;
  }

  @include media-breakpoint-up(lg) {
    padding: 0;
  }
}

.checkout-step-two__header {
  margin: 0 0 30px;
}

.checkout-step-two__headline {
  @include font-format(1.375rem, 1.625em);
}

.checkout-step__copy-additional {
  font-style: italic;
  margin-top: 1rem;
}

.campsite-type-selector {
  margin-bottom: 10px;

  @include media-breakpoint-up(xl) {
    margin-bottom: 30px;
  }
}

.campsite-type {
  @include font('gotham_mediumregular');

  align-items: center;
  display: flex;
  height: 60px;
  justify-content: center;
  margin: 0 0 10px;
  cursor: pointer;
  transition: background-color 0.4s ease;

  .no-touch &:hover,
  &.active {
    color: white;
    background: $primary-green;
  }
}
</style>
